import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { rexTechTheme } from '../Theme';
import { 
  Home,
  ClipboardList,
  BarChart,
  User,
  LogOut,
  Key,
  ChevronDown,
  Building
} from 'lucide-react';

const ClientHeader = () => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const clientNavItems = [
    { label: 'Dashboard', path: '/client/dashboard', icon: <Home size={18} /> },
    { label: 'Requests', path: '/client/requests', icon: <ClipboardList size={18} /> },
    { label: 'Reports', path: '/client/reports', icon: <BarChart size={18} /> },
      { label: 'Branches', path: '/client/branches', icon: <Building size={18} /> },
  ];

  const profileMenuItems = [
    { label: 'My Profile', icon: <User size={16} />, action: () => navigate('/client/profile') },
    { label: 'Change Password', icon: <Key size={16} />, action: () => navigate('/client/changepassword') },
    { label: 'Logout', icon: <LogOut size={16} />, action: () => handleLogout() },
  ];

  const handleLogout = () => {
    console.log('Logging out...');
    // Clear client session/tokens
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientId');
    navigate('/login');
  };

  return (
    <header style={{
      backgroundColor: rexTechTheme.colors.neutral.white,
      color: rexTechTheme.colors.primary.dark,
      padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.lg}`,
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
      borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '80px'
      }}>
        {/* Left Side: RexTech Logo Only */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          height: '100%'
        }}>
          <Link 
            to="/client/dashboard" 
            style={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              height: '100%'
            }}>
              <img 
                src="/logo.png" 
                alt="RexTech Logo"
                style={{
                  height: '100px',
                  width: '150px',
                  objectFit: 'contain'
                }}
              />
            </div>
          </Link>
        </div>

        {/* Center: Client Navigation (Desktop) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: rexTechTheme.spacing.md,
          marginLeft: rexTechTheme.spacing.xl
        }}>
          {clientNavItems.map((item) => (
            <Link
              key={item.label}
              to={item.path}
              style={{
                textDecoration: 'none',
                color: location.pathname === item.path 
                  ? rexTechTheme.colors.primary.main 
                  : rexTechTheme.colors.neutral.darkGray,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: `${rexTechTheme.spacing.sm} ${rexTechTheme.spacing.lg}`,
                borderRadius: rexTechTheme.borderRadius.medium,
                transition: 'all 0.3s ease',
                fontWeight: rexTechTheme.typography.fontWeight.medium,
                fontSize: rexTechTheme.typography.fontSize.regular,
                backgroundColor: location.pathname === item.path 
                  ? rexTechTheme.colors.primary.lightest 
                  : 'transparent',
                border: location.pathname === item.path 
                  ? `1px solid ${rexTechTheme.colors.primary.lighter}` 
                  : '1px solid transparent',
                ':hover': {
                  backgroundColor: rexTechTheme.colors.primary.lightest,
                  border: `1px solid ${rexTechTheme.colors.primary.lighter}`,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(30, 132, 73, 0.1)'
                }
              }}
            >
              <span style={{ 
                color: location.pathname === item.path 
                  ? rexTechTheme.colors.primary.main 
                  : rexTechTheme.colors.primary.light,
                display: 'flex',
                alignItems: 'center'
              }}>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </Link>
          ))}
        </div>

        {/* Right Side: Profile Dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.sm,
              padding: `${rexTechTheme.spacing.sm} ${rexTechTheme.spacing.lg}`,
              backgroundColor: rexTechTheme.colors.neutral.white,
              border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
              borderRadius: rexTechTheme.borderRadius.medium,
              cursor: 'pointer',
              fontSize: rexTechTheme.typography.fontSize.regular,
              fontWeight: rexTechTheme.typography.fontWeight.medium,
              transition: 'all 0.3s ease',
              color: rexTechTheme.colors.primary.dark,
              ':hover': {
                backgroundColor: rexTechTheme.colors.primary.lightest,
                borderColor: rexTechTheme.colors.primary.lighter,
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(30, 132, 73, 0.1)'
              }
            }}
          >
            <div style={{
              width: '36px',
              height: '36px',
              backgroundColor: rexTechTheme.colors.primary.lightest,
              border: `1px solid ${rexTechTheme.colors.primary.lighter}`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <User size={18} color={rexTechTheme.colors.primary.main} />
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{
                fontSize: rexTechTheme.typography.fontSize.regular,
                fontWeight: 600,
                color: rexTechTheme.colors.primary.dark
              }}>
                Client
              </div>
            </div>
            <ChevronDown size={16} style={{ 
              color: rexTechTheme.colors.primary.main,
              transition: 'transform 0.3s ease',
              transform: showProfileMenu ? 'rotate(180deg)' : 'rotate(0deg)'
            }} />
          </button>

          {/* Profile Dropdown Menu */}
          {showProfileMenu && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              backgroundColor: rexTechTheme.colors.neutral.white,
              borderRadius: rexTechTheme.borderRadius.medium,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
              border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
              minWidth: '220px',
              zIndex: 1000,
              overflow: 'hidden'
            }}>
              {/* Menu Items */}
              <div style={{
                padding: rexTechTheme.spacing.xs
              }}>
                {profileMenuItems.map((item, index) => (
                  <button
                    key={item.label}
                    onClick={() => {
                      item.action();
                      setShowProfileMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: rexTechTheme.spacing.md,
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: rexTechTheme.spacing.md,
                      color: rexTechTheme.colors.neutral.darkGray,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      fontWeight: rexTechTheme.typography.fontWeight.medium,
                      transition: 'all 0.2s ease',
                      borderRadius: rexTechTheme.borderRadius.small,
                      ':hover': {
                        backgroundColor: rexTechTheme.colors.primary.lightest,
                        color: rexTechTheme.colors.primary.main
                      }
                    }}
                  >
                    <span style={{ 
                      color: rexTechTheme.colors.primary.light,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default ClientHeader;